const _ = require("lodash");


/**
 * A synchronous helper function that checks whether the value is null, empty, or undefined
 * <br />
 * Dependencies: lodash
 * To Use: Pass in the value to check
 * @param {*} x Value to check
 * @return {boolean} returns true if the value is empty; otherwise it returns false.
 **/
function isEmpty(x) {
    return !x || _.isEmpty(x);
}

/**
 * A synchronous helper function that checks whether the value is an empty array or an array that contains an empty array or empty object
 * <br />
 * Dependencies: lodash
 * To Use: Pass in the value to check
 * @param {array} x Value to check
 * @return {boolean} returns true if the value is empty; otherwise it returns false.
 **/
function isEmptyArray(x) {
    return _.isArray(x) && x.every((item) => isEmptyArray(item) || isEmpty(item));
}

/**
 * A synchronous helper function that checks whether the object is null or undefined
 * <br />
 * To Use: Pass in the object to check
 * @param {object} obj Object to check
 * @return {boolean} returns true if the value is null or undefined; otherwise it returns false.
 **/
function exists(obj) {
    return obj !== null && obj !== undefined;
}

/**
 * A synchronous helper function that throws an error if the object is empty or contains any null or undefined keys
 * <br />
 * To Use: Pass in the object to check
 * @param {object} obj Object to check
 * @return {(boolean|object)} returns true if the object is empty or contains any null or undefined keys; otherwise it returns the object.
 **/
function throwIfKeysUndOrNull(obj) {
    if (_.isEmpty(obj)) {
        throw new Error("The object is empty or undefined");
    }

    Object.keys(obj).forEach(function thowIfKeysUndOrNullForEachKey(key) {
        if (obj[key] === undefined || obj[key] === null) {
            throw new Error(`Missing required key on object: ${key}`);
        }
    });

    return obj;
}

/**
 * A synchronous helper function that checks a value is not or does not contain null or empty values and contains only valid data types accepted by AWS:
 * string, number, boolean, date, array, object
 * <br />
 * To Use: Pass in the value to check
 * @param {*} obj Value to check
 * @return {(object|string|array)}
 * returns null if empty object is passed in,
 * returns an object if object passed in is a string, number, boolean, or date,
 * returns an object if a non-empty array is passed in,
 * returns a new object if an object with valid values is passed in
 **/
function stripObjectToAwsFormat(obj) {
    let newObj = {};

    if (obj === null || obj === undefined || obj === "") {
        return null;
    }

    if (_.isString(obj) || _.isNumber(obj) || _.isBoolean(obj) || _.isDate(obj)) {
        return obj;
    }

    if (_.isArray(obj)) {
        return obj.map((item) => stripObjectToAwsFormat(item))
            .filter((x) => x !== null);
    }

    Object.keys(obj).forEach(function stripObjectToAwsFormatForEachKey(key) {
        const value = obj[key];

        let res = stripObjectToAwsFormat(value);

        if (res !== null) {
            newObj[key] = res;
        }
    });

    return newObj;
}

/**
 * A synchronous helper function that replaces parts of a string with another value
 * <br />
 * To Use: Pass in the original string, the value to replace, the value to replace with
 * @param {string} str string to convert
 * @param {string} find value to be replaced
 * @param {string} replace value to replace with
 * @return {string} returns converted string
 **/
function replaceAll(str, find, replace) {
    return str.split(find).join(replace);
}

/**
 * A synchronous helper function that combines parameters into a single string
 * <br />
 * To Use: Pass in an iterable array or object
 * @param {(array|object)} params parameters to convert
 * @return {string} returns key as a string
 **/
function createKey(...params) {
    return params.map((param) => replaceAll(param.toString(), "-", "~"))
        .join("-");
}

/**
 * A synchronous helper function that generates a random integer
 * <br />
 * To Use: Pass in a range
 * @param {int} min lower end of range
 * @param {int} max upper end of range
 * @return {int} returns random integer
 **/
function generateRandomInt(min = 0, max = 5000000) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * An asynchronous helper function that lets you return a default value in case the request fails
 * <br />
 * To Use: Pass in a function and alternative return values
 * @param {func} func asynchronous function that makes an http request
 * @param {*} defaultValue value to return if func return null or undefined
 * @param {*} failedValue value to return if func returns an error
 * @return {*} returns the function's response if successful or alternative return values if not
 **/
function tryGet(func, defaultValue = undefined, failedValue = defaultValue) {
    try {
        let res = func();

        if (res === undefined || res === null) {
            return defaultValue;
        }

        return res;
    } catch (ignore) {
        return failedValue;
    }
}


/**
 * A synchronous helper function that updates a nested object by adding new properties at the specified path.
 * For example,
 * updateProperties({}, ["a", "b", "c"], { name: "anc", birthday: 123})
 * will return
 * { a: { b: {c: { name: "anc", birthday: 123 } } } }
 * <br />
 * To Use: Pass in target object, path array, properties object. Each element in the path array represents a successive tier in the nested object.
 * @param {object} obj target object
 * @param {array} path array of keys defining the path to where you want to add new properties
 * @param {object} props properties to merge with target object
 * @return {object}
 **/
function updateProperties(obj, path, props) {
    if (path.length === 0) {
        return Object.assign({}, obj, props);
    }
    const curPath = path.shift();
    const replaceObj = {};
    replaceObj[curPath] = updateProperties(obj[curPath] || {}, path, props);
    return Object.assign({}, obj, replaceObj);
}

function isTrue(bool) {
    if (_.isBoolean(bool)) {
        return bool;
    }

    return bool === "true";
}

function noop() {
    // NOOP
}

module.exports = {
    isEmpty,
    isEmptyArray,
    isTrue,
    exists,
    throwIfKeysUndOrNull,
    stripObjectToAwsFormat,
    replaceAll,
    createKey,
    generateRandomInt,
    tryGet,
    updateProperties,
    noop,
};
